import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { PageContent } from '../components/layout/PageContent';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { scoreboardApi, championshipApi } from '../lib/api';
import {
  Plus, Play, Pause, Trash2, Clock, Loader2, MonitorPlay, ArrowLeft, Check,
  RotateCcw, Save, Minus, X, RefreshCw, ListChecks
} from 'lucide-react';

const DURATION_OPTIONS = [
  { label: '02:00', value: 120 },
  { label: '03:00', value: 180 },
  { label: '04:00', value: 240 },
  { label: '05:00', value: 300 },
  { label: '06:00', value: 360 },
  { label: '07:00', value: 420 },
  { label: '08:00', value: 480 },
  { label: '10:00', value: 600 },
];

const RESULT_TYPES = [
  { value: 'points', label: 'Pontos' },
  { value: 'submission', label: 'Finalização' },
  { value: 'advantages', label: 'Vantagens' },
  { value: 'wo', label: 'WO' },
  { value: 'disqualification', label: 'Desclassificação' },
];

function formatTime(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

const STATUS_META = {
  queued: { label: 'Na fila', cls: 'bg-[#27272A] text-[#A1A1AA] border-[#3F3F46]' },
  active: { label: 'Em andamento', cls: 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/40' },
  finished: { label: 'Concluída', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/40' },
  sent: { label: 'Enviada', cls: 'bg-green-500/10 text-green-400 border-green-500/40' },
};

function ScoreBox({ label, value, colorClass, onMinus, onPlus, onReset, disabled, darkText }) {
  return (
    <div
      onClick={disabled ? undefined : onPlus}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onPlus();
        }
      }}
      title="Clique para +1"
      className={`flex-1 rounded-xl ${colorClass} relative overflow-hidden min-w-[110px] cursor-pointer select-none transition-transform hover:brightness-110 active:scale-[0.98] ${disabled ? 'opacity-60 cursor-default' : ''}`}
    >
      <span className={`absolute top-1.5 left-2.5 text-micro font-black uppercase tracking-widest ${darkText ? 'text-black/50' : 'text-white/80'}`}>{label}</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onReset(); }}
        disabled={disabled}
        title="Zerar"
        className="absolute top-1 right-1 w-6 h-6 rounded bg-black/25 hover:bg-black/50 text-white flex items-center justify-center transition-colors"
      >
        <X size={12} />
      </button>
      <div className={`text-6xl md:text-7xl font-black ${darkText ? 'text-black' : 'text-white'} text-center py-6 pt-9 leading-none`}>{value}</div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onMinus(); }}
        disabled={disabled || value <= 0}
        title="Diminuir (−1)"
        className="absolute bottom-1.5 left-1.5 w-7 h-7 rounded-md bg-black/30 hover:bg-black/55 disabled:opacity-30 text-white flex items-center justify-center transition-colors"
      >
        <Minus size={16} />
      </button>
    </div>
  );
}

/**
 * Format athlete name for scoreboard display.
 * - Capitalizes first letter of each word (title case)
 * - If the 2nd word is a particle (de/da/do/dos/das), includes it + the next name
 * - Otherwise takes just first name + 2nd word (surname)
 * Examples:
 *   ALICE DA SILVA DOS SANTOS  → Alice da Silva
 *   VICTORIA RODRIGUES DE OLIVEIRA → Victoria Rodrigues
 *   MARCOS SILVA              → Marcos Silva
 */
function AthletePanel({ athleteName, teamName, box1, box2, box3, accent, onBox, disabled, vsLabel }) {
  const { label: b1, value: v1, color: c1, field: f1, dark: d1 } = box1;
  const { label: b2, value: v2, color: c2, field: f2, dark: d2 } = box2;
  const { label: b3, value: v3, color: c3, field: f3, dark: d3 } = box3;
  return (
    <div className={`rounded-2xl border border-[#27272A] ${accent} p-5 md:p-7`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex-1 min-w-0">
          <p className="text-micro uppercase tracking-[0.3em] text-[#A1A1AA] font-bold mb-1">{vsLabel}</p>
          <h2 className="text-3xl md:text-5xl font-black text-white leading-tight break-words" data-testid="scoreboard-athlete-name">
            {athleteName || 'Atleta'}
          </h2>
          {teamName && <p className="text-sm md:text-base text-[#D4AF37]/80 mt-1.5 font-medium">{teamName}</p>}
        </div>
        {/* Botões de pontuação rápida: +2 +3 +4 / -2 -3 -4 */}
        <div className="flex flex-col gap-1 self-center">
          <div className="flex gap-1">
            {[2, 3, 4].map((n) => (
              <button
                key={`plus-${n}`}
                type="button"
                onClick={() => onBox(f1, n)}
                disabled={disabled}
                className="w-9 h-9 rounded-lg border-2 border-green-500 bg-transparent text-green-400 font-bold text-sm hover:bg-green-500/15 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-default"
                title={`+${n} pontos`}
              >+{n}</button>
            ))}
          </div>
          <div className="flex gap-1">
            {[2, 3, 4].map((n) => (
              <button
                key={`minus-${n}`}
                type="button"
                onClick={() => onBox(f1, -n)}
                disabled={disabled || v1 < n}
                className="w-9 h-9 rounded-lg border-2 border-green-500 bg-transparent text-green-400 font-bold text-sm hover:bg-green-500/15 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-default"
                title={`-${n} pontos`}
              >-{n}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 md:gap-3 w-full lg:w-auto">
          <ScoreBox label={b1} value={v1} colorClass={c1} darkText={d1} onMinus={() => onBox(f1, -1)} onPlus={() => onBox(f1, 1)} onReset={() => onBox(f1, -v1)} disabled={disabled} />
          <ScoreBox label={b2} value={v2} colorClass={c2} darkText={d2} onMinus={() => onBox(f2, -1)} onPlus={() => onBox(f2, 1)} onReset={() => onBox(f2, -v2)} disabled={disabled} />
          <ScoreBox label={b3} value={v3} colorClass={c3} darkText={d3} onMinus={() => onBox(f3, -1)} onPlus={() => onBox(f3, 1)} onReset={() => onBox(f3, -v3)} disabled={disabled} />
        </div>
      </div>
    </div>
  );
}

export default function ScoreboardPage() {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const contextId = params.id || searchParams.get('championship') || '';
  const [championships, setChampionships] = useState([]);
  const [selectedChamp, setSelectedChamp] = useState(contextId);
  const [mesas, setMesas] = useState(['Mesa 1']);
  const [selectedMesa, setSelectedMesa] = useState('Mesa 1');
  const [resultsMode, setResultsMode] = useState('manual');
  const [sessions, setSessions] = useState([]);
  const [loadingChamps, setLoadingChamps] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [bracketInput, setBracketInput] = useState('');
  const [loadingBracket, setLoadingBracket] = useState(false);

  // Modal de luta manual
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [modalForm, setModalForm] = useState({
    category_label: '',
    duration_seconds: '300',
    athlete1_name: '',
    athlete1_team: '',
    athlete2_name: '',
    athlete2_team: '',
  });
  const [savingModal, setSavingModal] = useState(false);

  // Placar em andamento
  const [activeId, setActiveId] = useState(null);
  const [sb, setSb] = useState(null); // { duration, elapsed, running, finished, score1..penalties2, winner_id, winner_name, result_type }
  const [swapped, setSwapped] = useState(false);
  const [sending, setSending] = useState(false);
  const [pendingDisqualification, setPendingDisqualification] = useState(null);
  const [disqualificationSnapshot, setDisqualificationSnapshot] = useState(null);

  const activeSession = sessions.find((s) => s._id === activeId) || null;
  const prevActiveRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await championshipApi.list();
        setChampionships(data);
        if (data.length > 0) {
          setSelectedChamp((cur) => (cur && data.some((c) => c._id === cur) ? cur : data[0]._id));
        }
      } catch {
        toast.error('Erro ao carregar campeonatos');
      } finally {
        setLoadingChamps(false);
      }
    };
    load();
  }, []);

  const loadSessions = useCallback(async (champId, mesa) => {
    if (!champId) return;
    setLoadingSessions(true);
    try {
      const { data } = await scoreboardApi.getSessions(champId, mesa ? { mesa } : {});
      setMesas(data.mesas?.length ? data.mesas : ['Mesa 1']);
      setSelectedMesa((cur) => (cur && data.mesas?.includes(cur) ? cur : data.mesas?.[0] || 'Mesa 1'));
      setResultsMode(data.results_mode || 'manual');
      setSessions(data.sessions || []);
    } catch {
      toast.error('Erro ao carregar fila do placar');
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    if (selectedChamp) loadSessions(selectedChamp, selectedMesa);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChamp]);

  useEffect(() => {
    if (selectedChamp) loadSessions(selectedChamp, selectedMesa);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMesa]);

  // Restaura o estado do placar ao trocar de sessão ativa
  useEffect(() => {
    if (activeSession && prevActiveRef.current !== activeSession._id) {
      prevActiveRef.current = activeSession._id;
      setSb({
        duration: activeSession.duration_seconds || 300,
        elapsed: activeSession.elapsed_seconds || 0,
        running: activeSession.timer_state === 'running',
        finished: activeSession.timer_state === 'finished' || activeSession.status === 'finished' || activeSession.status === 'sent',
        score1: activeSession.score1 || 0,
        score2: activeSession.score2 || 0,
        advantages1: activeSession.advantages1 || 0,
        advantages2: activeSession.advantages2 || 0,
        penalties1: activeSession.penalties1 || 0,
        penalties2: activeSession.penalties2 || 0,
        winner_id: activeSession.winner_id || null,
        winner_name: activeSession.winner_name || null,
        result_type: activeSession.result_type || 'points',
        penalty_mode: activeSession.penalty_mode || 'manual',
      });
      setSwapped(!!activeSession.swapped);
    }
    if (!activeSession) {
      prevActiveRef.current = null;
      setSb(null);
    }
  }, [activeSession]);

  // Persistência do placar (definida ANTES dos efeitos que a usam —
  // referenciá-la no array de dependências antes da declaração `const`
  // lançava ReferenceError e deixava a página /placar preta).
  const persist = useCallback(
    (patch) => {
      if (!selectedChamp || !activeSession) return;
      scoreboardApi.updateSession(selectedChamp, activeSession._id, patch).catch(() => {});
    },
    [selectedChamp, activeSession]
  );

  // Determina vencedor automaticamente por tempo (pontos → vantagens → menos punições → empate)
  const determineWinnerByTime = useCallback((state) => {
    if (!activeSession) return { winner: null, isDraw: true };
    const { score1, score2, advantages1, advantages2, penalties1, penalties2 } = state;

    // 1º Critério: Pontos
    if (score1 > score2) return { winner: { id: activeSession.athlete1_id, name: activeSession.athlete1_name }, isDraw: false };
    if (score2 > score1) return { winner: { id: activeSession.athlete2_id, name: activeSession.athlete2_name }, isDraw: false };

    // 2º Critério: Vantagens
    if (advantages1 > advantages2) return { winner: { id: activeSession.athlete1_id, name: activeSession.athlete1_name }, isDraw: false };
    if (advantages2 > advantages1) return { winner: { id: activeSession.athlete2_id, name: activeSession.athlete2_name }, isDraw: false };

    // 3º Critério: Menos punições
    if (penalties1 < penalties2) return { winner: { id: activeSession.athlete1_id, name: activeSession.athlete1_name }, isDraw: false };
    if (penalties2 < penalties1) return { winner: { id: activeSession.athlete2_id, name: activeSession.athlete2_name }, isDraw: false };

    // 4º: Empate absoluto — decisão humana
    return { winner: null, isDraw: true };
  }, [activeSession]);

  // Cronômetro
  const sbRef = useRef(null);
  useEffect(() => {
    sbRef.current = sb;
  }, [sb]);

  useEffect(() => {
    if (!activeSession || !sb?.running || sb?.finished || pendingDisqualification) return;
    const t = setInterval(() => {
      setSb((prev) => {
        if (!prev) return prev;
        const nextElapsed = prev.elapsed + 1;
        if (nextElapsed >= prev.duration) {
          // Determina vencedor automaticamente por tempo
          const { winner } = determineWinnerByTime(prev);
          return {
            ...prev,
            elapsed: prev.duration,
            running: false,
            finished: true,
            ...(winner ? { winner_id: winner.id, winner_name: winner.name, result_type: 'points' } : {}),
          };
        }
        return { ...prev, elapsed: nextElapsed };
      });
      // Persiste o cronômetro a cada segundo para a Tela do Placar
      const cur = sbRef.current;
      if (cur && !cur.finished) {
        const nextElapsed = Math.min(cur.duration, cur.elapsed + 1);
        if (nextElapsed >= cur.duration) {
          // Determina vencedor automaticamente por tempo (persist)
          const { winner } = determineWinnerByTime(cur);
          persist({
            timer_state: 'finished',
            elapsed_seconds: cur.duration,
            status: 'finished',
            ...(winner ? { winner_id: winner.id, winner_name: winner.name, result_type: 'points' } : {}),
          });
        } else {
          persist({
            timer_state: 'running',
            elapsed_seconds: nextElapsed,
            status: 'active',
          });
        }
      }
    }, 1000);
    return () => clearInterval(t);
  }, [activeSession, sb?.running, sb?.finished, persist, pendingDisqualification, determineWinnerByTime]);

  // Persiste o fim automático do cronômetro (tempo esgotado)
  useEffect(() => {
    if (activeSession && sb && !sb.running && sb.finished && sb.elapsed >= sb.duration) {
      persist({ timer_state: 'finished', elapsed_seconds: sb.duration, status: 'finished' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sb?.finished, sb?.elapsed, sb?.running]);

  const handleSearchBracket = async (e) => {
    e.preventDefault();
    const value = parseInt(bracketInput, 10);
    if (!value) {
      toast.error('Digite o Nº da chave (ou o código)');
      return;
    }
    setLoadingBracket(true);
    try {
      const { data } = await scoreboardApi.createSession(selectedChamp, {
        mesa: selectedMesa,
        source: 'bracket',
        bracket_number: value,
      });
      const created = data.created || [];
      setSessions((prev) => [...prev, ...created]);
      setBracketInput('');
      toast.success(
        created.length > 1
          ? `Chave #${data.bracket_number} carregada — ${created.length} lutas na fila`
          : `Chave #${data.bracket_number} carregada — 1 luta na fila`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Chave não encontrada');
    } finally {
      setLoadingBracket(false);
    }
  };

  const openAddModal = () => {
    setEditingSession(null);
    setModalForm({
      category_label: '',
      duration_seconds: '300',
      athlete1_name: '',
      athlete1_team: '',
      athlete2_name: '',
      athlete2_team: '',
    });
    setModalOpen(true);
  };

  const openEditModal = (session) => {
    setEditingSession(session);
    setModalForm({
      category_label: session.category_label || '',
      duration_seconds: String(session.duration_seconds || 300),
      athlete1_name: session.athlete1_name || '',
      athlete1_team: session.athlete1_team || '',
      athlete2_name: session.athlete2_name || '',
      athlete2_team: session.athlete2_team || '',
    });
    setModalOpen(true);
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    setSavingModal(true);
    try {
      const payload = {
        mesa: selectedMesa,
        category_label: modalForm.category_label || 'Luta Avulsa',
        duration_seconds: parseInt(modalForm.duration_seconds, 10) || 300,
        athlete1_name: modalForm.athlete1_name,
        athlete1_team: modalForm.athlete1_team,
        athlete2_name: modalForm.athlete2_name,
        athlete2_team: modalForm.athlete2_team,
      };
      if (editingSession) {
        await scoreboardApi.updateSession(selectedChamp, editingSession._id, payload);
        toast.success('Luta atualizada');
      } else {
        const { data } = await scoreboardApi.createSession(selectedChamp, { ...payload, source: 'manual' });
        setSessions((prev) => [...prev, ...(data.created || [])]);
        toast.success('Luta de exibição adicionada');
      }
      setModalOpen(false);
    } catch {
      toast.error('Erro ao salvar luta');
    } finally {
      setSavingModal(false);
    }
  };

  const handleDelete = async (session) => {
    if (!window.confirm('Remover esta luta da fila?')) return;
    try {
      await scoreboardApi.deleteSession(selectedChamp, session._id);
      setSessions((prev) => prev.filter((s) => s._id !== session._id));
      if (activeId === session._id) {
        setActiveId(null);
        setSb(null);
      }
      toast.success('Luta removida da fila');
    } catch {
      toast.error('Erro ao remover luta');
    }
  };

  const startPlay = async (session) => {
    setActiveId(session._id);
    try {
      await scoreboardApi.updateSession(selectedChamp, session._id, { status: 'active' });
      setSessions((prev) => prev.map((s) => (s._id === session._id ? { ...s, status: 'active' } : s)));
    } catch {
      /* estado local já cobre */
    }
  };

  const goBackToQueue = () => {
    setActiveId(null);
    setSb(null);
    loadSessions(selectedChamp, selectedMesa);
  };

  const openDisplayWindow = () => {
    if (!selectedChamp) return;
    const url = `/placar/exibir?championship=${selectedChamp}&mesa=${encodeURIComponent(selectedMesa)}`;
    window.open(url, '_blank');
  };

  const startTimer = () => {
    setSb((prev) => ({ ...prev, running: true, finished: false }));
    persist({ timer_state: 'running', elapsed_seconds: sb.elapsed, status: 'active' });
  };

  const pauseTimer = () => {
    setSb((prev) => {
      persist({ timer_state: 'paused', elapsed_seconds: prev.elapsed });
      return { ...prev, running: false };
    });
  };

  const finishFight = () => {
    setSb((prev) => ({ ...prev, running: false, finished: true }));
    persist({ timer_state: 'finished', elapsed_seconds: sb.elapsed, status: 'finished' });
  };

  const resetTimer = () => {
    setSb((prev) => ({
      ...prev,
      elapsed: 0,
      running: false,
      finished: false,
      score1: 0,
      score2: 0,
      advantages1: 0,
      advantages2: 0,
      penalties1: 0,
      penalties2: 0,
      winner_id: null,
      winner_name: null,
    }));
    persist({
      timer_state: 'idle',
      elapsed_seconds: 0,
      status: 'active',
      score1: 0,
      score2: 0,
      advantages1: 0,
      advantages2: 0,
      penalties1: 0,
      penalties2: 0,
      winner_id: '',
      winner_name: '',
    });
  };

  // Aplica regras automáticas de punições (chamado pelo bump quando penalty_mode === 'automatic')
  const applyPenaltyRules = useCallback((side, newCount) => {
    if (sb.penalty_mode !== 'automatic') return;
    const opponent = side === '1' ? '2' : '1';

    if (newCount === 2) {
      // 2ª punição → +1 vantagem ao adversário
      const advField = `advantages${opponent}`;
      const newVal = (sb[advField] || 0) + 1;
      setSb(prev => ({ ...prev, [advField]: newVal }));
      persist({ [advField]: newVal });
    } else if (newCount === 3) {
      // 3ª punição → +2 pontos ao adversário
      const scoreField = `score${opponent}`;
      const newVal = (sb[scoreField] || 0) + 2;
      setSb(prev => ({ ...prev, [scoreField]: newVal }));
      persist({ [scoreField]: newVal });
    } else if (newCount === 4) {
      // 4ª punição → snapshot + modal de confirmação
      const snapshot = { ...sb };
      setDisqualificationSnapshot(snapshot);
      setPendingDisqualification({ side, snapshot });
    }
  }, [sb, persist]);

  const bump = (field, delta) => {
    if (sb.finished || pendingDisqualification) return;
    const next = { ...sb, [field]: Math.max(0, (sb[field] || 0) + delta) };
    setSb(next);
    persist({
      [field]: next[field],
      elapsed_seconds: next.elapsed,
      timer_state: next.running ? 'running' : 'paused',
    });

    // Aplica regras automáticas se for punição e modo automático
    if (field === 'penalties1' || field === 'penalties2') {
      const side = field === 'penalties1' ? '1' : '2';
      applyPenaltyRules(side, next[field]);
    }
  };

  const setWinner = (who) => {
    setSb((prev) => ({
      ...prev,
      winner_id: who === 'a1' ? activeSession.athlete1_id : activeSession.athlete2_id,
      winner_name: who === 'a1' ? activeSession.athlete1_name : activeSession.athlete2_name,
    }));
  };

  // Confirmar desclassificação por 4ª punição
  const confirmDisqualification = () => {
    if (!pendingDisqualification) return;
    const { side } = pendingDisqualification;
    const opponent = side === '1' ? '2' : '1';
    const opponentId = opponent === '1' ? activeSession.athlete1_id : activeSession.athlete2_id;
    const opponentName = opponent === '1' ? activeSession.athlete1_name : activeSession.athlete2_name;
    const penaltiesField = `penalties${side}`;

    setSb(prev => ({
      ...prev,
      [penaltiesField]: 4,
      running: false,
      finished: true,
      winner_id: opponentId,
      winner_name: opponentName,
      result_type: 'disqualification',
    }));
    persist({
      [penaltiesField]: 4,
      timer_state: 'finished',
      status: 'finished',
      winner_id: opponentId,
      winner_name: opponentName,
      result_type: 'disqualification',
      elapsed_seconds: sb.elapsed,
    });
    setPendingDisqualification(null);
    setDisqualificationSnapshot(null);
  };

  // Cancelar desclassificação — restaurar snapshot anterior
  const cancelDisqualification = () => {
    if (!disqualificationSnapshot) return;
    const snap = disqualificationSnapshot;
    setSb({ ...snap });
    persist({
      penalties1: snap.penalties1,
      penalties2: snap.penalties2,
      timer_state: snap.running ? 'running' : 'paused',
      elapsed_seconds: snap.elapsed,
      winner_id: '',
      winner_name: '',
      status: 'active',
    });
    setPendingDisqualification(null);
    setDisqualificationSnapshot(null);
  };

  const handleSaveResult = async () => {
    if (activeSession.source === 'bracket' && !sb.winner_id) {
      toast.error('Selecione o vencedor da luta');
      return;
    }
    setSending(true);
    try {
      await scoreboardApi.finalizeSession(selectedChamp, activeSession._id, {
        winner_id: sb.winner_id || null,
        winner_name: sb.winner_name || null,
        result_type: sb.result_type || 'points',
        score1: sb.score1 || 0,
        score2: sb.score2 || 0,
        advantages1: sb.advantages1 || 0,
        advantages2: sb.advantages2 || 0,
        penalties1: sb.penalties1 || 0,
        penalties2: sb.penalties2 || 0,
      });
      toast.success(activeSession.source === 'bracket' ? 'Resultado enviado para a Mesa Central!' : 'Luta de exibição encerrada (nada gravado)');
      goBackToQueue();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao enviar resultado');
    } finally {
      setSending(false);
    }
  };

  const mesaSessions = sessions; // já filtrada pelo backend via mesa param

  const modeLabel =
    resultsMode === 'automatic' ? 'Automático' : resultsMode === 'hybrid' ? 'Híbrido' : 'Manual';

  if (loadingChamps) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
      </PageContent>
    );
  }

  if (!selectedChamp) {
    return (
      <PageContent>
        {/* ETAPA 9 — fora da densidade: esta tela é operada sob pressão no dia
            do campeonato e foi pedido para não mudar. */}
        <div className="escala-original space-y-6" data-testid="scoreboard-page">
          <h1 className="text-3xl font-bold text-white font-['Chivo']">Placar Eletrônico</h1>
          <div className="rounded-2xl border border-[#27272A] bg-[#141414] p-16 text-center">
            <MonitorPlay className="w-16 h-16 text-[#71717A] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">Nenhum campeonato disponível. Crie um campeonato para usar o placar.</p>
          </div>
        </div>
      </PageContent>
    );
  }

  // ============ VISÃO DO PLACAR (luta em andamento) ============
  if (activeSession && sb) {
    const panelA = swapped ? activeSession.athlete2_name : activeSession.athlete1_name;
    const panelATeam = swapped ? activeSession.athlete2_team : activeSession.athlete1_team;
    const panelB = swapped ? activeSession.athlete1_name : activeSession.athlete2_name;
    const panelBTeam = swapped ? activeSession.athlete1_team : activeSession.athlete2_team;
    const aBox = swapped ? 'a2' : 'a1';
    const bBox = aBox === 'a1' ? 'a2' : 'a1';
    const remaining = Math.max(0, sb.duration - sb.elapsed);
    const isManual = activeSession.source !== 'bracket';

    const boxesFor = (side) => {
      const p = side === 'a1'
        ? { score1: sb.score1, advantages1: sb.advantages1, penalties1: sb.penalties1 }
        : { score2: sb.score2, advantages2: sb.advantages2, penalties2: sb.penalties2 };
      return {
        pontos: p[`score${side === 'a1' ? 1 : 2}`] || 0,
        vantagens: p[`advantages${side === 'a1' ? 1 : 2}`] || 0,
        punicoes: p[`penalties${side === 'a1' ? 1 : 2}`] || 0,
      };
    };

    // Bloqueia pontuação quando: finalizada OU antes de clicar "Começar" (idle com elapsed=0)
    const scoreDisabled = sb.finished || (!sb.running && sb.elapsed === 0);

    return (
      <PageContent>
        <div className="space-y-5" data-testid="scoreboard-live">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-white font-['Chivo'] flex items-center gap-2">
                <MonitorPlay className="text-[#D4AF37]" size={24} /> Placar Eletrônico
              </h1>
              <p className="text-xs text-[#A1A1AA] mt-1">
                {activeSession.category_label || 'Luta'} · {activeSession.round_label} · Chave #
                {activeSession.bracket_number || '—'} · {isManual ? 'Exibição' : modeLabel}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={openDisplayWindow} className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black" title="Abrir a tela do placar em janela cheia (TV)">
                <MonitorPlay size={16} className="mr-2" /> Abrir Tela
              </Button>
              <Button variant="outline" onClick={goBackToQueue} className="border-[#27272A] text-[#A1A1AA] hover:text-white">
                <ArrowLeft size={16} className="mr-2" /> Voltar à fila
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <AthletePanel
              athleteName={panelA}
              teamName={panelATeam}
              vsLabel={swapped ? 'Atleta 2' : 'Atleta 1'}
              accent="bg-gradient-to-br from-[#0B1220] to-[#16233B] border-blue-500/30"
              box1={{ label: 'Pontos', value: boxesFor(aBox).pontos, color: 'bg-[#22C55E]/90', field: `score${aBox === 'a1' ? 1 : 2}` }}
              box2={{ label: 'Vantagens', value: boxesFor(aBox).vantagens, color: 'bg-[#EAB308]/90', field: `advantages${aBox === 'a1' ? 1 : 2}`, dark: true }}
              box3={{ label: 'Punições', value: boxesFor(aBox).punicoes, color: 'bg-[#EF4444]/90', field: `penalties${aBox === 'a1' ? 1 : 2}` }}
              onBox={bump}
              disabled={scoreDisabled}
            />
            <AthletePanel
              athleteName={panelB}
              teamName={panelBTeam}
              vsLabel={swapped ? 'Atleta 1' : 'Atleta 2'}
              accent="bg-gradient-to-br from-[#1A1A1A] to-[#23252B] border-[#D4AF37]/30"
              box1={{ label: 'Pontos', value: boxesFor(bBox).pontos, color: 'bg-[#3F3F46]/90', field: `score${bBox === 'a1' ? 1 : 2}` }}
              box2={{ label: 'Vantagens', value: boxesFor(bBox).vantagens, color: 'bg-[#EAB308]/90', field: `advantages${bBox === 'a1' ? 1 : 2}`, dark: true }}
              box3={{ label: 'Punições', value: boxesFor(bBox).punicoes, color: 'bg-[#EF4444]/90', field: `penalties${bBox === 'a1' ? 1 : 2}` }}
              onBox={bump}
              disabled={scoreDisabled}
            />
          </div>

          {/* Barra de controle + cronômetro */}
          <div className="rounded-2xl border border-[#27272A] bg-[#141414] p-5">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex flex-wrap gap-2">
                {!sb.running && !sb.finished && (
                  <Button onClick={startTimer} data-testid="scoreboard-start" className="border-2 border-green-500 bg-transparent text-green-400 hover:bg-green-500 hover:text-black font-bold">
                    <Play size={16} className="mr-2" /> Começar!
                  </Button>
                )}
                {sb.running && !sb.finished && (
                  <Button onClick={pauseTimer} data-testid="scoreboard-pause" className="border-2 border-[#EAB308] bg-transparent text-[#EAB308] hover:bg-[#EAB308] hover:text-black font-bold">
                    <Pause size={16} className="mr-2" /> Pausar
                  </Button>
                )}
                {!sb.finished && (
                  <Button onClick={finishFight} disabled={!sb.running && sb.elapsed === 0 || !!pendingDisqualification} data-testid="scoreboard-finish" className="border-2 border-red-500 bg-transparent text-red-400 hover:bg-red-500 hover:text-white font-bold disabled:opacity-30 disabled:cursor-default">
                    Finalizar luta
                  </Button>
                )}
                {!sb.finished && (
                  <Button onClick={resetTimer} disabled={(!sb.running && sb.elapsed === 0) || !!pendingDisqualification} className="border-2 border-white/40 bg-transparent text-white hover:bg-white hover:text-black font-bold disabled:opacity-30 disabled:cursor-default">
                    <RotateCcw size={16} className="mr-2" /> Reiniciar
                  </Button>
                )}
                <Button onClick={() => { const next = !swapped; setSwapped(next); persist({ swapped: next }); }} className="border-2 border-[#D4AF37] bg-transparent text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-bold">
                  <RefreshCw size={16} className="mr-2" /> Trocar cores
                </Button>
              </div>
              <div className="ml-auto flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-micro uppercase tracking-widest text-[#71717A] font-bold">Regras:</span>
                  <Select
                    value={sb.penalty_mode || 'manual'}
                    onValueChange={(v) => {
                      setSb(prev => ({ ...prev, penalty_mode: v }));
                      persist({ penalty_mode: v });
                    }}
                    disabled={sb.finished}
                  >
                    <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-white h-8 w-[180px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      <SelectItem value="manual" className="text-white text-xs">Manual</SelectItem>
                      <SelectItem value="automatic" className="text-white text-xs">Aplicar regras</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className={`font-mono text-5xl md:text-6xl font-black ${sb.running ? 'text-[#D4AF37]' : 'text-[#71717A]'}`} data-testid="scoreboard-timer">
                  {formatTime(remaining)}
                </div>
              </div>
            </div>

            {sb.finished && (
              <div className="mt-5 pt-5 border-t border-[#27272A] space-y-4 animate-fade-in-up">
                {isManual ? (
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <p className="text-[#A1A1AA] text-sm flex-1">
                      Luta de exibição encerrada. Nenhum resultado oficial será gravado.
                    </p>
                    <Button onClick={handleSaveResult} disabled={sending} className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check size={16} className="mr-2" />} Encerrar e voltar
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-sm font-bold text-[#A1A1AA] uppercase tracking-wider">Vencedor:</span>
                      <Button
                        onClick={() => setWinner('a1')}
                        variant="outline"
                        className={sb.winner_id === activeSession.athlete1_id ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] font-bold' : 'border-[#27272A] text-white'}
                      >
                        {activeSession.athlete1_name}
                      </Button>
                      <Button
                        onClick={() => setWinner('a2')}
                        variant="outline"
                        className={sb.winner_id === activeSession.athlete2_id ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] font-bold' : 'border-[#27272A] text-white'}
                      >
                        {activeSession.athlete2_name}
                      </Button>
                      <Select value={sb.result_type} onValueChange={(v) => setSb((prev) => ({ ...prev, result_type: v }))}>
                        <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-white w-[200px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          {RESULT_TYPES.map((rt) => (
                            <SelectItem key={rt.value} value={rt.value} className="text-white hover:bg-[#27272A]">{rt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="ml-auto">
                        <Button onClick={handleSaveResult} disabled={sending || !sb.winner_id} data-testid="scoreboard-save" className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold text-base px-6 py-3">
                          {sending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Salvar e Enviar Resultado
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Dialog de confirmação da 4ª punição */}
          {pendingDisqualification && (
            <Dialog open={!!pendingDisqualification} onOpenChange={() => {}}>
              <DialogContent className="bg-[#141414] border-[#EF4444] max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-[#EF4444] font-['Chivo'] text-center">
                    ⚠️ 4ª PUNIÇÃO ATINGIDA
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-center">
                  <p className="text-white font-bold text-lg">
                    LUTA FINALIZADA POR PUNIÇÕES — CONFIRMA?
                  </p>
                  <p className="text-[#A1A1AA] text-sm">
                    {pendingDisqualification.side === '1'
                      ? activeSession.athlete1_name
                      : activeSession.athlete2_name}{' '}
                    atingiu a 4ª punição. O adversário seria declarado vencedor por desclassificação.
                  </p>
                  <div className="flex gap-3 justify-center pt-2">
                    <Button
                      onClick={confirmDisqualification}
                      className="bg-[#EF4444] text-white hover:bg-red-600 font-bold px-6"
                      data-testid="scoreboard-confirm-dq"
                    >
                      SIM, FINALIZAR LUTA
                    </Button>
                    <Button
                      onClick={cancelDisqualification}
                      variant="outline"
                      className="border-[#27272A] text-white hover:border-[#D4AF37] font-bold px-6"
                      data-testid="scoreboard-cancel-dq"
                    >
                      NÃO, CONTINUAR LUTA
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </PageContent>
    );
  }

  // ============ VISÃO DA FILA ============
  return (
    <PageContent>
      <div className="space-y-6" data-testid="scoreboard-page">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Chivo'] flex items-center gap-2">
              <MonitorPlay className="text-[#D4AF37]" size={24} /> Placar Eletrônico
            </h1>
            <p className="text-[#A1A1AA] mt-1">
              Modo de operação: <span className="text-[#D4AF37] font-semibold">{modeLabel}</span> — digite o Nº da chave da folha do Staff para puxar as lutas
            </p>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <Select value={selectedChamp} onValueChange={setSelectedChamp}>
              <SelectTrigger data-testid="scoreboard-champ-select" className="bg-[#1A1A1A] border-[#27272A] text-white w-[240px]">
                <SelectValue placeholder="Campeonato..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                {championships.map((c) => (
                  <SelectItem key={c._id} value={c._id} className="text-white hover:bg-[#27272A]">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedMesa} onValueChange={setSelectedMesa}>
              <SelectTrigger data-testid="scoreboard-mesa-select" className="bg-[#1A1A1A] border-[#27272A] text-white w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                {mesas.map((m) => (
                  <SelectItem key={m} value={m} className="text-white hover:bg-[#27272A]">{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={openDisplayWindow} data-testid="scoreboard-open-display" className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold whitespace-nowrap" title="Abrir a tela do placar em janela cheia (TV)">
              <MonitorPlay size={16} className="mr-2" /> Abrir Tela
            </Button>
            <Button variant="outline" onClick={() => loadSessions(selectedChamp, selectedMesa)} className="border-[#27272A] text-[#A1A1AA] hover:text-white px-3" title="Atualizar fila">
              <RefreshCw size={16} />
            </Button>
          </div>
        </div>

        {/* Barra de ações */}
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <Label className="text-[#A1A1AA]">Tipo de placar</Label>
            <Select value="bjj">
              <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-white w-full md:w-[280px] mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="bjj" className="text-white">BJJ (Jiu-Jitsu Brasileiro)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <form onSubmit={handleSearchBracket} className="flex-1">
            <Label className="text-[#A1A1AA]">Nº da chave (folha do Staff)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={bracketInput}
                onChange={(e) => setBracketInput(e.target.value)}
                placeholder="Ex.: 12"
                inputMode="numeric"
                data-testid="scoreboard-bracket-input"
                className="bg-[#1A1A1A] border-[#27272A] text-white"
              />
              <Button type="submit" disabled={loadingBracket} data-testid="scoreboard-load-bracket" className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold whitespace-nowrap">
                {loadingBracket ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus size={16} className="mr-1" />} Carregar chave
              </Button>
            </div>
          </form>
          <div className="flex gap-2">
            <Button onClick={openAddModal} data-testid="scoreboard-add-manual" className="bg-[#27272A] text-white hover:bg-[#3F3F46] font-bold">
              <Plus size={16} className="mr-2" /> Adicionar nova luta
            </Button>
          </div>
        </div>

        {/* Fila de fichas */}
        <div className="space-y-3">
          {loadingSessions ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
          ) : mesaSessions.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#27272A] bg-[#141414] p-16 text-center">
              <ListChecks className="w-14 h-14 text-[#71717A] mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-1">Fila vazia</h3>
              <p className="text-[#A1A1AA]">Digite o Nº da chave acima ou adicione uma luta de exibição para começar.</p>
            </div>
          ) : (
            mesaSessions.map((s, idx) => {
              const st = STATUS_META[s.status] || STATUS_META.queued;
              const done = s.status === 'finished' || s.status === 'sent';
              return (
                <div
                  key={s._id}
                  data-testid="scoreboard-session-card"
                  className={`rounded-2xl border bg-[#141414] p-4 flex flex-col lg:flex-row lg:items-center gap-4 transition-all ${
                    activeId === s._id ? 'border-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-[#27272A] hover:border-[#D4AF37]/30'
                  } ${done ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <span className="text-[#71717A] font-mono text-lg font-bold w-8 text-center">#{idx + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <span className="text-xs font-bold text-white uppercase tracking-wide">{s.category_label || 'Luta Avulsa'}</span>
                        <span className="text-micro uppercase tracking-widest bg-[#1A1A1A] text-[#D4AF37] border border-[#D4AF37]/20 px-2 py-0.5 rounded">
                          {s.round_label}
                        </span>
                        {s.bracket_number && (
                          <span className="text-micro uppercase tracking-widest bg-[#1A1A1A] text-[#A1A1AA] px-2 py-0.5 rounded">
                            Chave #{s.bracket_number}
                          </span>
                        )}
                        <span className={`text-micro uppercase tracking-widest px-2 py-0.5 rounded border ${st.cls}`}>{st.label}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-2">
                        <div className="min-w-0">
                          <p className="font-bold text-white truncate">{s.athlete1_name}</p>
                          {s.athlete1_team && <p className="text-xs text-[#A1A1AA] italic truncate">{s.athlete1_team}</p>}
                        </div>
                        <div className="hidden sm:block text-micro font-black text-[#D4AF37] px-2">VS</div>
                        <div className="min-w-0 sm:text-right">
                          <p className="font-bold text-white truncate">{s.athlete2_name}</p>
                          {s.athlete2_team && <p className="text-xs text-[#A1A1AA] italic truncate">{s.athlete2_team}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 lg:gap-3">
                    <span className="flex items-center gap-1.5 text-sm text-[#A1A1AA] font-mono">
                      <Clock size={16} className="text-[#D4AF37]" /> {formatTime(s.duration_seconds)}
                    </span>
                    <div className="flex flex-col gap-1.5">
                      {s.status !== 'sent' && s.status !== 'finished' && (
                        <Button onClick={() => startPlay(s)} size="sm" className="bg-green-600 text-white hover:bg-green-500 px-2.5" title="Abrir no placar" data-testid="scoreboard-play-session">
                          <Play size={16} />
                        </Button>
                      )}
                      {s.source === 'manual' && s.status !== 'sent' && (
                        <Button onClick={() => openEditModal(s)} size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:text-white px-2.5" title="Editar">
                          <RotateCcw size={16} />
                        </Button>
                      )}
                    </div>
                    <Button onClick={() => handleDelete(s)} size="sm" className="bg-red-600 text-white hover:bg-red-500 px-2.5" title="Remover da fila">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Modal: nova luta / editar (somente exibição) */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#141414] border-[#27272A] max-w-lg flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white font-['Chivo']">{editingSession ? 'Editar luta' : 'Adicionar nova luta'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleModalSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Categoria:</Label>
              <Input
                value={modalForm.category_label}
                onChange={(e) => setModalForm({ ...modalForm, category_label: e.target.value })}
                placeholder="ADULTO · Azul · LEVE · Feminino"
                data-testid="scoreboard-modal-category"
                className="bg-[#1A1A1A] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Tempo de luta (MM:SS):</Label>
              <div className="relative">
                <Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <Select value={modalForm.duration_seconds} onValueChange={(v) => setModalForm({ ...modalForm, duration_seconds: v })}>
                  <SelectTrigger data-testid="scoreboard-modal-duration" className="bg-[#1A1A1A] border-[#27272A] text-white pl-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    {DURATION_OPTIONS.map((d) => (
                      <SelectItem key={d.value} value={String(d.value)} className="text-white hover:bg-[#27272A]">{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-xl border border-[#27272A] bg-[#1A1A1A]/50 p-4 space-y-3">
              <p className="text-center text-xs uppercase tracking-widest text-[#A1A1AA] font-bold">Primeiro atleta</p>
              <Input
                value={modalForm.athlete1_name}
                onChange={(e) => setModalForm({ ...modalForm, athlete1_name: e.target.value })}
                placeholder="Nome do atleta"
                data-testid="scoreboard-modal-a1-name"
                className="bg-[#1A1A1A] border-[#27272A] text-white"
              />
              <Input
                value={modalForm.athlete1_team}
                onChange={(e) => setModalForm({ ...modalForm, athlete1_team: e.target.value })}
                placeholder="Equipe"
                className="bg-[#1A1A1A] border-[#27272A] text-white"
              />
            </div>
            <div className="rounded-xl border border-[#27272A] bg-[#1A1A1A]/50 p-4 space-y-3">
              <p className="text-center text-xs uppercase tracking-widest text-[#A1A1AA] font-bold">Segundo atleta</p>
              <Input
                value={modalForm.athlete2_name}
                onChange={(e) => setModalForm({ ...modalForm, athlete2_name: e.target.value })}
                placeholder="Nome do atleta"
                data-testid="scoreboard-modal-a2-name"
                className="bg-[#1A1A1A] border-[#27272A] text-white"
              />
              <Input
                value={modalForm.athlete2_team}
                onChange={(e) => setModalForm({ ...modalForm, athlete2_team: e.target.value })}
                placeholder="Equipe"
                data-testid="scoreboard-modal-a2-team"
                className="bg-[#1A1A1A] border-[#27272A] text-white"
              />
            </div>
            <div className="sticky bottom-0 z-10 -mx-6 -mb-6 flex justify-end gap-2 border-t border-[#27272A] bg-[#141414] px-6 py-3">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="bg-[#d9534f] text-white hover:bg-[#c9302c] border-transparent">
                Cancelar
              </Button>
              <Button type="submit" disabled={savingModal} data-testid="scoreboard-modal-ok" className="bg-[#22C55E] text-black hover:bg-[#16A34A] font-bold">
                {savingModal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check size={16} className="mr-1" />} OK
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageContent>
  );
}
